import i18n from '@/i18n'
import router from '@/router'
import type { ApiError } from '@/services/http/types'
import { useAuthStore } from '@/stores/auth.store'
import type { AxiosError } from 'axios'
import { toast } from 'vue-sonner'

export function errorInterceptor(
  error: AxiosError<{ message?: string; errors?: Record<string, string[]>; error?: Record<string, string[]> | string }>,
) {
  const t = i18n.global.t
  const status = error.response?.status ?? 0
  const serverMessage =
    error.response?.data?.message ??
    (typeof error.response?.data?.error === 'string' ? error.response?.data?.error : undefined)
  const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
  const isCanceled = error.code === 'ERR_CANCELED' || error.name === 'CanceledError'

  // Silently swallow user-cancelled requests (AbortController)
  if (isCanceled) {
    return Promise.reject({
      status: 0,
      message: 'canceled',
      code: 'ERR_CANCELED',
    } satisfies ApiError)
  }

  const errorObj = error.response?.data?.error
  const apiError: ApiError = {
    status,
    message: serverMessage ?? mapStatusToMessage(status, isTimeout, t),
    // Support both `errors` (standard Laravel) and `error` (this backend's 422 shape)
    errors:
      error.response?.data?.errors ??
      (errorObj !== null && typeof errorObj === 'object' ? (errorObj as Record<string, string[]>) : undefined),
  }

  // Quiet 401 on the bootstrap probe — "not signed in yet" is normal there.
  const requestUrl = error.config?.url ?? ''
  const isMeProbe = status === 401 && requestUrl.endsWith('/auth/me')
  const isMissingAiChatHistory =
    status === 404 &&
    /\/ai-chat\/sessions\/[^/]+\/messages(?:\?|$)/.test(requestUrl)

  switch (status) {
    case 401: {
      const auth = useAuthStore()
      auth.clear()
      if (isMeProbe) break
      const current = router.currentRoute.value
      if (current.name !== 'login' && current.meta.requiresAuth) {
        toast.error(t('errors.sessionExpired'))
        router.push({ name: 'login', query: { redirect: current.fullPath } })
      }
      break
    }
    case 403: {
      const isLoginRoute = router.currentRoute.value.name === 'login'
      if (isLoginRoute) {
        toast.error(apiError.message)
      } else {
        toast.error(t('errors.forbidden'))
        router.push({ name: 'forbidden' })
      }
      break
    }
    case 404:
      if (isMissingAiChatHistory) break
      toast.error(apiError.message)
      break
    case 409: {
      // Silent redirect for incomplete onboarding — no toast
      const errBody = error.response?.data?.error as Record<string, unknown> | undefined
      if (errBody?.code === 'ONBOARDING_INCOMPLETE') {
        router.push({ name: 'onboarding-landing' })
        break
      }
      toast.error(apiError.message)
      break
    }
    case 422:
      // validation errors — let the caller render field-level errors
      break
    case 429: {
      const retryAfter = error.response?.headers?.['retry-after']
      const seconds = typeof retryAfter === 'string' ? Number(retryAfter) : NaN
      toast.error(
        Number.isFinite(seconds) && seconds > 0
          ? t('errors.rateLimitedWithRetry', { seconds })
          : t('errors.rateLimited'),
      )
      break
    }
    case 0:
      toast.error(isTimeout ? t('errors.timeout') : t('errors.network'))
      break
    default:
      if (status >= 500) toast.error(t('errors.server'))
      else toast.error(apiError.message)
  }

  return Promise.reject(apiError)
}

function mapStatusToMessage(
  status: number,
  isTimeout: boolean,
  t: (k: string, named?: Record<string, unknown>) => string,
): string {
  if (isTimeout) return t('errors.timeout')
  if (status === 0) return t('errors.network')
  if (status === 401) return t('errors.unauthorized')
  if (status === 403) return t('errors.forbidden')
  if (status === 404) return t('errors.notFound')
  if (status === 422) return t('errors.validation')
  if (status === 429) return t('errors.rateLimited')
  if (status >= 500) return t('errors.server')
  return t('errors.generic')
}
